"use client";

import { mapAuthApiUserToStoreUser } from "@/features/auth/types/signup";
import type { AuthLoginResponse } from "@/features/auth/types/signup";
import { getPostAuthDestination } from "@/lib/authDashboardGate";
import { REDIRECT_PARAM, safeDashboardRedirect } from "@/lib/safe-redirect";
import { authToken } from "@/services/auth-token";
import {
  pickLoginApiUser,
  pickLoginToken,
} from "@/features/auth/lib/authResponsePickers";
import type { User } from "@/store/auth.store";

export type ApplyAuthLoginResult =
  | { ok: true; user: User }
  | { ok: false; message: string };

export function applyAuthLoginEnvelope(
  resBody: AuthLoginResponse,
): ApplyAuthLoginResult {
  const token = pickLoginToken(resBody);
  if (!token) {
    return { ok: false, message: "No access token in response." };
  }
  authToken.set(token);
  const apiUser = pickLoginApiUser(resBody);
  if (!apiUser) {
    return { ok: false, message: "Invalid user in response." };
  }
  const user = mapAuthApiUserToStoreUser(apiUser);
  return { ok: true, user };
}

/** When true, caller should navigate to returned path instead of applying tokens. */
export function getEmailVerificationPathIfNeeded(
  resBody: AuthLoginResponse,
): string | null {
  if (resBody.data?.requiresEmailVerification !== true) return null;
  const email = resBody.data?.user?.email?.trim();
  if (!email) return null;
  return `/verify-identity?email=${encodeURIComponent(email)}&flow=email-verification`;
}

/**
 * Sends the user where they were headed before login — e.g. an invoice a
 * contractor routed to their dashboard from a Fixrli Direct link — but only
 * once the normal gate says they may use the dashboard at all. Someone still
 * onboarding or waiting on beta access goes there first, as before.
 */
export function navigateAfterLogin(
  user: User,
  router: { replace: (href: string) => void },
): void {
  const destination = getPostAuthDestination(user);
  const canUseDashboard =
    destination.startsWith("/dashboard") &&
    destination !== "/dashboard/beta-access";

  const requested =
    typeof window === "undefined"
      ? null
      : safeDashboardRedirect(
          new URLSearchParams(window.location.search).get(REDIRECT_PARAM),
        );

  router.replace(canUseDashboard && requested ? requested : destination);
}
